/**
 * Caps the internal canvas bitmap size so 4K TVs do not fill/rasterize 8M+ pixels per frame.
 * Display size stays viewport-sized via CSS (stretch); visuals stay sharp enough for lounge distance.
 */

const DEFAULT_MAX_W = 1920;
const DEFAULT_MAX_H = 1080;

function readOverrideDims(): { maxW: number; maxH: number } {
  if (typeof window === 'undefined') {
    return { maxW: DEFAULT_MAX_W, maxH: DEFAULT_MAX_H };
  }
  try {
    if (window.localStorage.getItem('tvCanvasRaw') === '1') {
      return { maxW: Number.POSITIVE_INFINITY, maxH: Number.POSITIVE_INFINITY };
    }
  } catch {
    /* ignore */
  }
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('tvCanvasRaw') === '1') {
      return { maxW: Number.POSITIVE_INFINITY, maxH: Number.POSITIVE_INFINITY };
    }
    const mw = Number.parseInt(q.get('tvCanvasMaxW') ?? '', 10);
    const mh = Number.parseInt(q.get('tvCanvasMaxH') ?? '', 10);
    if (Number.isFinite(mw) && mw >= 320 && Number.isFinite(mh) && mh >= 240) {
      return { maxW: mw, maxH: mh };
    }
  } catch {
    /* ignore */
  }
  return { maxW: DEFAULT_MAX_W, maxH: DEFAULT_MAX_H };
}

export function computeTvCanvasBufferSize(
  viewportCssW: number,
  viewportCssH: number,
): {
  bufferW: number;
  bufferH: number;
  cssW: number;
  cssH: number;
} {
  const { maxW, maxH } = readOverrideDims();
  const cssW = Math.max(1, Math.floor(viewportCssW));
  const cssH = Math.max(1, Math.floor(viewportCssH));
  if (!Number.isFinite(maxW) || !Number.isFinite(maxH)) {
    return {
      bufferW: cssW,
      bufferH: cssH,
      cssW,
      cssH,
    };
  }
  const scale = Math.min(1, maxW / cssW, maxH / cssH);
  const bufferW = Math.max(1, Math.floor(cssW * scale));
  const bufferH = Math.max(1, Math.floor(cssH * scale));
  return {
    bufferW,
    bufferH,
    cssW,
    cssH,
  };
}
